import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Container,
  Link,
  ListItemIcon,
} from '@mui/material';
import {
  AccountTreeRounded as TreeIcon,
  PeopleRounded as DirectoryIcon,
  CakeRounded as BirthdayIcon,
  ForumRounded as MessageIcon,
  LogoutRounded as LogoutIcon,
  MoreVertRounded as MoreIcon,
  DownloadRounded as DownloadIcon,
  EmailRounded as EmailIcon,
} from '@mui/icons-material';
import { removeAuthToken } from '../apollo/client';
import { GET_CLOUDINARY_CONFIG, GET_PERSONS, GET_RELATIONSHIPS, GET_HOUSEHOLDS } from '../apollo/queries';
import { setCloudName } from '../utils/cloudinary';
import { exportAllData } from '../utils/exportData';

const navItems = [
  { label: 'Tree', path: '/tree', icon: <TreeIcon /> },
  { label: 'Directory', path: '/directory', icon: <DirectoryIcon /> },
  { label: 'Birthdays', path: '/birthdays', icon: <BirthdayIcon /> },
  { label: 'Messages', path: '/message-board', icon: <MessageIcon /> },
];

function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [anchorEl, setAnchorEl] = useState(null);

  // Load Cloudinary config on mount
  const { data: cloudinaryData } = useQuery(GET_CLOUDINARY_CONFIG);

  // Lazy queries for export
  const [fetchPersons] = useLazyQuery(GET_PERSONS);
  const [fetchRelationships] = useLazyQuery(GET_RELATIONSHIPS);
  const [fetchHouseholds] = useLazyQuery(GET_HOUSEHOLDS);

  useEffect(() => {
    if (cloudinaryData?.cloudinaryConfig?.cloudName) {
      setCloudName(cloudinaryData.cloudinaryConfig.cloudName);
    }
  }, [cloudinaryData]);

  const currentNav = navItems.findIndex((item) => location.pathname.startsWith(item.path));

  const handleLogout = () => {
    removeAuthToken();
    navigate('/login');
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExportData = async () => {
    handleMenuClose();
    try {
      const [personsResult, relationshipsResult, householdsResult] = await Promise.all([
        fetchPersons(),
        fetchRelationships(),
        fetchHouseholds(),
      ]);

      exportAllData(
        personsResult.data?.persons || [],
        relationshipsResult.data?.relationships || [],
        householdsResult.data?.households || []
      );
    } catch (error) {
      console.error('Error exporting data:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 1 }}>
            <img
              src="https://res.cloudinary.com/dws0tlgkc/image/upload/v1764189766/rft1-just-logo_fwxvsl.png"
              alt="Logo"
              style={{ height: 32, width: 'auto' }}
            />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
              Reinhard Family Tree
            </Typography>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
              {navItems.map((item) => (
                <IconButton
                  key={item.path}
                  color="inherit"
                  onClick={() => navigate(item.path)}
                  sx={{
                    opacity: location.pathname.startsWith(item.path) ? 1 : 0.7,
                    '&:hover': { opacity: 1 },
                  }}
                  title={item.label}
                >
                  {item.icon}
                </IconButton>
              ))}
            </Box>
          )}

          <IconButton color="inherit" onClick={handleMenuOpen}>
            <MoreIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem onClick={handleExportData}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              Export Data
            </MenuItem>
            <MenuItem
              component="a"
              href="mailto:gray.reinhard@gmail.com"
              onClick={handleMenuClose}
            >
              <ListItemIcon>
                <EmailIcon fontSize="small" />
              </ListItemIcon>
              Contact Gray
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pb: isMobile ? 10 : 2,
          pt: 2,
          overflow: 'auto',
        }}
      >
        <Container maxWidth="lg" sx={{ height: '100%' }}>
          <Outlet />
        </Container>
      </Box>

      {/* Footer - only show on desktop */}
      {!isMobile && (
        <Box
          component="footer"
          sx={{
            py: 2,
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Questions or suggestions? Email{' '}
            <Link href="mailto:gray.reinhard@gmail.com" underline="hover">
              gray.reinhard@gmail.com
            </Link>
          </Typography>
        </Box>
      )}

      {isMobile && (
        <BottomNavigation
          value={currentNav}
          onChange={(_, newValue) => navigate(navItems[newValue].path)}
          showLabels
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          }}
        >
          {navItems.map((item) => (
            <BottomNavigationAction
              key={item.path}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      )}
    </Box>
  );
}

export default Layout;

